import { useQuery } from '@tanstack/react-query';
import { decodeEventLog, encodeEventTopics, pad, parseAbi, type Hex, type Log } from 'viem';
import { MARKET } from '../../shared/market.generated';
import type { Address } from '../../shared/types';
import { publicClient } from './queries';

const eventsAbi = parseAbi([
  'event Supply(address indexed reserve, address user, address indexed onBehalfOf, uint256 amount, uint16 indexed referralCode)',
  'event Withdraw(address indexed reserve, address indexed user, address indexed to, uint256 amount)',
  'event Borrow(address indexed reserve, address user, address indexed onBehalfOf, uint256 amount, uint8 interestRateMode, uint256 borrowRate, uint16 indexed referralCode)',
  'event Repay(address indexed reserve, address indexed user, address indexed repayer, uint256 amount, bool useATokens)',
  'event ReserveUsedAsCollateralEnabled(address indexed reserve, address indexed user)',
  'event ReserveUsedAsCollateralDisabled(address indexed reserve, address indexed user)',
  'event LiquidationCall(address indexed collateralAsset, address indexed debtAsset, address indexed user, uint256 debtToCover, uint256 liquidatedCollateralAmount, address liquidator, bool receiveAToken)',
]);

export type HistoryAction = 'Supply' | 'Withdraw' | 'Borrow' | 'Repay' | 'Collateral enabled' | 'Collateral disabled' | 'Liquidation';
export interface HistoryItem {
  id: string;
  action: HistoryAction;
  symbol: string;
  decimals: number;
  amount: bigint | null;
  /** Liquidations also name the debt that was repaid. */
  secondary?: { symbol: string; decimals: number; amount: bigint };
  timestamp: number;
  block: number;
  tx: Hex;
}

const topic0 = (name: string): Hex => encodeEventTopics({ abi: eventsAbi, eventName: name as never })[0] as Hex;
const bySymbol = (address: string) => MARKET.assets.find((a) => a.address.toLowerCase() === address.toLowerCase());
const ACTION: Record<string, HistoryAction> = {
  Supply: 'Supply', Withdraw: 'Withdraw', Borrow: 'Borrow', Repay: 'Repay',
  ReserveUsedAsCollateralEnabled: 'Collateral enabled', ReserveUsedAsCollateralDisabled: 'Collateral disabled', LiquidationCall: 'Liquidation',
};

/**
 * The user's activity straight from the pool's event logs: no indexer involved. In every user-facing event but the
 * liquidation the account sits in the second indexed slot, so two filtered queries cover everything.
 */
async function loadHistory(user: Address): Promise<HistoryItem[]> {
  const who = pad(user, { size: 32 });
  const fromBlock = BigInt(MARKET.deployBlock);
  const common = ['Supply', 'Withdraw', 'Borrow', 'Repay', 'ReserveUsedAsCollateralEnabled', 'ReserveUsedAsCollateralDisabled'].map(topic0);
  const request = (topics: (Hex | Hex[] | null)[]) =>
    publicClient.request({ method: 'eth_getLogs', params: [{ address: MARKET.pool, fromBlock: `0x${fromBlock.toString(16)}`, toBlock: 'latest', topics }] }) as Promise<(Log & { blockTimestamp?: Hex })[]>;
  const [a, b] = await Promise.all([request([common, null, who]), request([[topic0('LiquidationCall')], null, null, who])]);

  const items: HistoryItem[] = [];
  for (const log of [...a, ...b]) {
    try {
      const ev = decodeEventLog({ abi: eventsAbi, data: log.data, topics: log.topics as [Hex, ...Hex[]] });
      const args = ev.args as Record<string, unknown>;
      const reserve = bySymbol((args.reserve ?? args.collateralAsset) as string);
      if (!reserve) continue;
      const item: HistoryItem = {
        id: `${log.transactionHash}-${log.logIndex}`,
        action: ACTION[ev.eventName]!,
        symbol: reserve.symbol,
        decimals: reserve.decimals,
        amount: ev.eventName === 'LiquidationCall' ? (args.liquidatedCollateralAmount as bigint) : ((args.amount as bigint | undefined) ?? null),
        timestamp: log.blockTimestamp ? Number(BigInt(log.blockTimestamp)) : 0,
        block: Number(BigInt(log.blockNumber as unknown as Hex)),
        tx: log.transactionHash as Hex,
      };
      if (ev.eventName === 'LiquidationCall') {
        const debt = bySymbol(args.debtAsset as string);
        if (debt) item.secondary = { symbol: debt.symbol, decimals: debt.decimals, amount: args.debtToCover as bigint };
      }
      items.push(item);
    } catch { /* an event this build does not know: skip it */ }
  }
  const recent = items.sort((x, y) => y.block - x.block || (x.id < y.id ? 1 : -1)).slice(0, MAX_ITEMS);
  await fillTimestamps(recent);
  return recent;
}

const MAX_ITEMS = 100;

/**
 * This chain's nodes leave `blockTimestamp` on logs at zero, so block times are fetched separately: one JSON-RPC
 * batch per 50 distinct blocks. A failure only costs the time labels, never the list itself.
 */
async function fillTimestamps(items: HistoryItem[]): Promise<void> {
  const blocks = [...new Set(items.filter((x) => !x.timestamp).map((x) => x.block))];
  const times = new Map<number, number>();
  try {
    for (let i = 0; i < blocks.length; i += 50) {
      const chunk = blocks.slice(i, i + 50);
      const res = await fetch(MARKET.rpc, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(chunk.map((b, id) => ({ jsonrpc: '2.0', id, method: 'eth_getBlockByNumber', params: [`0x${b.toString(16)}`, false] }))),
      });
      const body = (await res.json()) as { id: number; result?: { timestamp?: Hex } }[];
      for (const r of body) if (r.result?.timestamp) times.set(chunk[r.id]!, Number(BigInt(r.result.timestamp)));
    }
  } catch { /* keep block numbers as the fallback label */ }
  for (const x of items) if (!x.timestamp) x.timestamp = times.get(x.block) ?? 0;
}

export const useHistory = (user: Address | null) =>
  useQuery({ queryKey: ['history', user], queryFn: () => loadHistory(user!), enabled: !!user, staleTime: 15_000 });
